/**
 * Shared @dnd-kit setup for library / topic / assignment reorder grids.
 */
import { useMemo, useState } from "react";

import {
  defaultDropAnimationSideEffects,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type DropAnimation,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";

export const SORTABLE_DROP_ANIMATION: DropAnimation = {
  duration: 680,
  easing: "cubic-bezier(0.33, 0.1, 0.25, 1)",
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: "0" } },
  }),
};

export const SORTABLE_TRANSITION = {
  duration: 620,
  easing: "cubic-bezier(0.33, 0.1, 0.25, 1)",
} as const;

/** Pointer + keyboard sensors with a small drag threshold. */
export function useSortableSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
}

/** Rewrite 1-based sort_order after a reorder. */
export function renumberSortOrder<T extends { sort_order: number }>(
  items: T[]
): T[] {
  return items.map((item, index) => ({ ...item, sort_order: index + 1 }));
}

type SortableListOptions<T> = {
  items: T[];
  getId: (item: T) => string;
  onItemsChange: (items: T[]) => void;
  onMoved?: (id: string) => void;
  /** Optional remap after arrayMove (e.g. renumber sort_order). */
  transformMoved?: (items: T[]) => T[];
};

/**
 * Active/over ids + drag handlers for a flat SortableContext list.
 */
export function useSortableListState<T>({
  items,
  getId,
  onItemsChange,
  onMoved,
  transformMoved,
}: SortableListOptions<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const ids = useMemo(() => items.map(getId), [items, getId]);

  const reset = () => {
    setActiveId(null);
    setOverId(null);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    setOverId(String(active.id));
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    setOverId(over ? String(over.id) : null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const movedId = String(active.id);
    reset();
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(movedId);
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(items, from, to);
    onItemsChange(transformMoved ? transformMoved(next) : next);
    onMoved?.(movedId);
  };

  /** Live order map while dragging (keys = getId). */
  const previewOrders = (
    getOrder: (item: T) => number,
    project: (items: T[]) => T[]
  ): Map<string, number> => {
    const from = activeId ? ids.indexOf(activeId) : -1;
    const to = overId ? ids.indexOf(overId) : -1;
    const projected =
      from >= 0 && to >= 0 ? project(arrayMove(items, from, to)) : items;
    return new Map(projected.map(item => [getId(item), getOrder(item)]));
  };

  return {
    activeId,
    overId,
    ids,
    reset,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    previewOrders,
  };
}
